import { useEffect, useMemo, useRef, useState } from 'react'
import { useQueries, useQuery } from '@tanstack/react-query'
import { CalendarView } from '@/components/teacher-request/CalendarView'
import { DayLessonsPanel } from '@/components/teacher-request/DayLessonsPanel'
import { TeacherLetterDirectory } from '@/components/teacher/TeacherLetterDirectory'
import { mergeMonthDays } from '@/hooks/useCalendarData'
import { useTeacherRequestConfig } from '@/hooks/useTeacherRequestConfig'
import { adminOnBehalfApi, type OnBehalfTeacher } from '@/services/api/adminOnBehalf'
import { sneakPeekApi, type SneakPeekDay, type SneakPeekGlance, type SneakPeekQueryRow } from '@/services/api/sneakPeek'
import { DayCell } from '@/pages/sneak-peek/narrowing'
import { SneakPeekPrintView } from '@/pages/sneak-peek/printView'
import { SavedQueries } from '@/pages/sneak-peek/savedQueries'
import { clipAllowed, deriveNarrowing, narrowingKey, selectionValue, splitLessonIdentity, type NarrowedDay } from '@/pages/sneak-peek/selection'
import { ThresholdField } from '@/pages/sneak-peek/threshold'
import { getDatesInRange, isLessonInPast, monthsInRange, todayLocalISO } from '@/lib/date-utils'
import type { DateRange, SelectionMode } from '@/types/teacher-request'

const WEEKDAYS = ['Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag']

function spanDays(from: string, to: string) {
  const start = new Date(`${from}T00:00:00`)
  const end = new Date(`${to}T00:00:00`)
  return Math.round((end.getTime() - start.getTime()) / 86400000) + 1
}

function weekdayIndex(iso: string) {
  return (new Date(`${iso}T00:00:00`).getDay() + 6) % 7
}

function weekdaysBetween(from: string, to: string) {
  if (!from || !to || to < from) return []
  const dates: string[] = []
  const cursor = new Date(`${from}T00:00:00`)
  const end = new Date(`${to}T00:00:00`)
  while (cursor <= end) {
    const day = cursor.getDay()
    if (day !== 0 && day !== 6) {
      const month = String(cursor.getMonth() + 1).padStart(2, '0')
      const date = String(cursor.getDate()).padStart(2, '0')
      dates.push(`${cursor.getFullYear()}-${month}-${date}`)
    }
    cursor.setDate(cursor.getDate() + 1)
  }
  return dates
}

type CalendarLesson = {
  entry_id: string
  date: string
  time_slot_id: string
  time_slot_number: number
  start_time: string
  end_time: string
  subject_name: string | null
  room_name: string | null
  klass_name: string | null
  is_requestable: boolean
  existing_request_id: string | null
  existing_request_status: string | null
}

type CalendarDay = {
  date: string
  is_school_day?: boolean
  lessons?: CalendarLesson[]
}

type LessonSel = {
  entryId: string
  date: string
  timeSlotId: string
  startTime?: string
  endTime?: string
}

function lessonKey(lesson: { entryId: string; date: string; startTime?: string }) {
  return `${lesson.entryId}|${lesson.date}|${lesson.startTime || ''}`
}

function isSelectableLesson(lesson: CalendarLesson) {
  return !!lesson.is_requestable && !isLessonInPast(lesson.date, lesson.end_time)
}

function toSelectedLesson(lesson: CalendarLesson): LessonSel {
  return {
    entryId: lesson.entry_id,
    date: lesson.date,
    timeSlotId: lesson.time_slot_id,
    startTime: lesson.start_time,
    endTime: lesson.end_time,
  }
}

function requestableOn(days: CalendarDay[], date: string) {
  return (days.find((day) => day.date === date)?.lessons ?? []).filter(isSelectableLesson)
}

function lessonsFor(
  days: CalendarDay[],
  dates: string[],
  removed: string[],
  pin: NarrowedDay[] | null,
) {
  const narrowed = new Map((pin ?? []).map((row) => [row.date, new Set(row.lessons)]))
  const selected: LessonSel[] = []
  for (const date of dates) {
    if (removed.includes(date)) continue
    const allowed = pin ? narrowed.get(date) : undefined
    for (const lesson of requestableOn(days, date)) {
      if (allowed && ![...allowed].some((token) => clipAllowed(token, lesson.entry_id, lesson.start_time))) continue
      selected.push(toSelectedLesson(lesson))
    }
  }
  return selected
}

function sameLessons(left: LessonSel[], right: LessonSel[]) {
  if (left.length !== right.length) return false
  const keys = new Set(left.map(lessonKey))
  return right.every((lesson) => keys.has(lessonKey(lesson)))
}

function withoutRemovedDates(days: SneakPeekDay[], removed: string[]) {
  const gone = new Set(removed)
  return days
    .filter((day) => !gone.has(day.date))
    .map((day) => ({
      ...day,
      packages: (day.packages ?? []).map((row) => ({
        ...row,
        other_days: (row.other_days ?? []).filter((date) => !gone.has(date)),
      })),
    }))
}

function selectionStamp(value: {
  teacher_id: string
  date_from: string
  date_to: string
  selected_dates?: string[]
  narrowed_days?: { date: string; lessons: string[] }[]
} | null) {
  if (!value) return ''
  return [
    value.teacher_id,
    value.date_from,
    value.date_to,
    [...(value.selected_dates || [])].sort().join(','),
    narrowingKey(value.narrowed_days),
  ].join('|')
}

function frozenSelection(data: {
  teacher_id: string
  date_from: string
  date_to: string
  selected_dates: string[]
  narrowed_days: { date: string; lessons: string[] }[]
}) {
  const removed = data.selected_dates?.length
    ? weekdaysBetween(data.date_from, data.date_to).filter((date) => !data.selected_dates.includes(date))
    : []
  const remaining = weekdaysBetween(data.date_from, data.date_to).filter((date) => !removed.includes(date))
  if (!remaining.length) return null
  return selectionValue(
    data.teacher_id,
    data.date_from,
    data.date_to,
    removed.length ? remaining : [],
    data.narrowed_days || [],
  )
}

function seriesMinimum(days: SneakPeekDay[]) {
  const counts = days
    .filter((day) => day.counts !== false && day.count != null)
    .map((day) => day.count as number)
  if (!counts.length) return null
  return Math.min(...counts)
}

export function SneakPeekPage() {
  const now = new Date()
  const { requestConfig } = useTeacherRequestConfig()
  const [teacherSearch, setTeacherSearch] = useState('')
  const [teacher, setTeacher] = useState<OnBehalfTeacher | null>(null)
  const [year, setYear] = useState(now.getFullYear())
  const [month, setMonth] = useState(now.getMonth() + 1)
  const [selectedDate, setSelectedDate] = useState('')
  const [selectionMode, setSelectionMode] = useState<SelectionMode>('single')
  const [dateRange, setDateRange] = useState<DateRange | null>(null)
  const [rangeStep, setRangeStep] = useState<'from' | 'to'>('from')
  const [result, setResult] = useState<SneakPeekGlance | null>(null)
  const [spanError, setSpanError] = useState<string | null>(null)
  const [removedDates, setRemovedDates] = useState<string[]>([])
  const [pinnedNarrowing, setPinnedNarrowing] = useState<NarrowedDay[] | null>(null)
  const [selectedLessons, setSelectedLessons] = useState<LessonSel[]>([])
  const [queries, setQueries] = useState<SneakPeekQueryRow[]>([])
  const [openQueryId, setOpenQueryId] = useState<string | null>(null)
  const [openVersionId, setOpenVersionId] = useState<string | null>(null)
  const [openedSelection, setOpenedSelection] = useState<{
    teacherId: string
    dateFrom: string
    dateTo: string
    selectedDates: string[]
    narrowedKey: string
  } | null>(null)
  const [fresh, setFresh] = useState(false)
  const [openedSavedAt, setOpenedSavedAt] = useState<string | null>(null)
  const [holdGlance, setHoldGlance] = useState(false)
  const [threshold, setThreshold] = useState<number | null>(null)
  const [draft, setDraft] = useState('')
  const [glanceNonce, setGlanceNonce] = useState(0)
  const [printOpen, setPrintOpen] = useState(false)
  const [saving, setSaving] = useState(false)
  const [boundStamp, setBoundStamp] = useState<string | null>(null)
  const holdGlanceRef = useRef(holdGlance)
  holdGlanceRef.current = holdGlance

  const teachersQ = useQuery({
    queryKey: ['sneak-peek-teachers', teacherSearch],
    queryFn: () => adminOnBehalfApi.listTeachers(teacherSearch || undefined),
  })
  const monthQ = useQuery({
    queryKey: ['sneak-peek-month', teacher?.id, year, month],
    queryFn: () => adminOnBehalfApi.month(teacher!.id, year, month),
    enabled: !!teacher,
  })

  const from = selectionMode === 'single' ? selectedDate : dateRange?.from || ''
  const to = selectionMode === 'single' ? selectedDate : dateRange?.to || ''
  const extraMonths = useMemo(() => {
    if (!teacher || !from || !to || to < from) return []
    return monthsInRange(from, to).filter((item) => !(item.year === year && item.month === month))
  }, [teacher, from, to, year, month])
  const extraMonthQueries = useQueries({
    queries: extraMonths.map((item) => ({
      queryKey: ['sneak-peek-month', teacher?.id, item.year, item.month],
      queryFn: () => adminOnBehalfApi.month(teacher!.id, item.year, item.month),
      enabled: !!teacher,
    })),
  })
  const calendarDays = useMemo(
    () => mergeMonthDays<CalendarDay>([monthQ.data, ...extraMonthQueries.map((query) => query.data)]),
    [monthQ.data, extraMonthQueries],
  )
  const panelDates = useMemo(
    () => (from && to && to >= from
      ? (selectionMode === 'single' ? [selectedDate] : getDatesInRange(from, to))
      : []),
    [from, to, selectionMode, selectedDate],
  )
  const monthsReady = !from || !to || extraMonthQueries.every((query) => query.isFetched)
  const remainingDates = weekdaysBetween(from, to).filter((date) => !removedDates.includes(date))
  const selection = teacher && from && to && remainingDates.length
    ? selectionValue(
      teacher.id,
      from,
      to,
      removedDates.length ? remainingDates : [],
      pinnedNarrowing ?? [],
    )
    : null

  useEffect(() => {
    let cancelled = false
    sneakPeekApi.listQueries()
      .then((data) => {
        if (!cancelled) setQueries(data.queries)
      })
      .catch(() => {
        if (!cancelled) setQueries([])
      })
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    let cancelled = false
    sneakPeekApi.getThreshold()
      .then((data) => {
        if (cancelled) return
        setThreshold(data.threshold)
        setDraft(String(data.threshold))
      })
      .catch(() => {
        if (!cancelled) setDraft('')
      })
    return () => {
      cancelled = true
    }
  }, [])

  function commitThreshold() {
    const value = Number(draft)
    if (!Number.isInteger(value) || value < 1) {
      setDraft(threshold == null ? '' : String(threshold))
      return
    }
    if (value === threshold) return
    sneakPeekApi.updateThreshold(value)
      .then((data) => {
        setThreshold(data.threshold)
        setDraft(String(data.threshold))
        setGlanceNonce((n) => n + 1)
      })
      .catch(() => {
        setDraft(threshold == null ? '' : String(threshold))
      })
  }

  function loadFrozen(data: {
    teacher_id: string
    absent: string
    date_from: string
    date_to: string
    selected_dates: string[]
    narrowed_days: { date: string; lessons: string[] }[]
    report: SneakPeekGlance | null
  }) {
    setHoldGlance(true)
    const known = (teachersQ.data?.teachers ?? []).find((item) => item.id === data.teacher_id)
    setTeacher(known ?? {
      id: data.teacher_id,
      last_name: data.absent,
      first_name: '',
      teacher_code: null,
      display_name: data.absent,
    })
    if (data.date_from === data.date_to) {
      setSelectionMode('single')
      setSelectedDate(data.date_from)
      setDateRange(null)
    } else {
      setSelectionMode('range')
      setSelectedDate('')
      setDateRange({ from: data.date_from, to: data.date_to })
    }
    const start = new Date(`${data.date_from}T00:00:00`)
    setYear(start.getFullYear())
    setMonth(start.getMonth() + 1)
    const savedDates = data.selected_dates || []
    setRemovedDates(
      savedDates.length
        ? weekdaysBetween(data.date_from, data.date_to).filter((date) => !savedDates.includes(date))
        : [],
    )
    setPinnedNarrowing(data.narrowed_days?.length ? data.narrowed_days : null)
    setSpanError(null)
    setResult(data.report)
    setBoundStamp(selectionStamp(frozenSelection(data)))
  }

  function refreshQueries() {
    return sneakPeekApi.listQueries().then((data) => setQueries(data.queries))
  }

  function rememberOpened(data: {
    teacher_id: string
    date_from: string
    date_to: string
    selected_dates: string[]
    narrowed_days: { date: string; lessons: string[] }[]
    saved_at?: string
  }) {
    setOpenedSavedAt(data.saved_at || null)
    setOpenedSelection({
      teacherId: data.teacher_id,
      dateFrom: data.date_from,
      dateTo: data.date_to,
      selectedDates: data.selected_dates || [],
      narrowedKey: narrowingKey(data.narrowed_days),
    })
  }

  function openSaved(id: string) {
    sneakPeekApi.openQuery(id).then((data) => {
      setOpenQueryId(id)
      setOpenVersionId(null)
      setFresh(false)
      rememberOpened(data)
      loadFrozen(data)
    })
  }

  function openVersion(queryId: string, versionId: string) {
    sneakPeekApi.openVersion(queryId, versionId).then((data) => {
      setOpenQueryId(queryId)
      setOpenVersionId(versionId)
      setFresh(false)
      rememberOpened(data)
      loadFrozen(data)
    })
  }

  function selectionUnchanged() {
    if (!openedSelection || !teacher || !selection) return false
    return selection.teacher_id === openedSelection.teacherId
      && selection.date_from === openedSelection.dateFrom
      && selection.date_to === openedSelection.dateTo
      && [...(selection.selected_dates || [])].sort().join(',') === [...openedSelection.selectedDates].sort().join(',')
      && narrowingKey(selection.narrowed_days) === openedSelection.narrowedKey
  }

  function showFresh(data: SneakPeekGlance) {
    setHoldGlance(true)
    setResult(data)
    setBoundStamp(selectionStamp(selection))
    setFresh(true)
  }

  function recalculate() {
    if (!openQueryId || !teacher || !from || !to) return
    if (selectionUnchanged()) {
      const call = openVersionId
        ? sneakPeekApi.recalculateVersion(openQueryId, openVersionId)
        : sneakPeekApi.recalculateQuery(openQueryId)
      call.then(showFresh).catch(() => {})
      return
    }
    if (!selection) return
    sneakPeekApi.glance(selection).then(showFresh).catch(() => {})
  }

  function noteSelectionChange() {
    if (fresh) {
      setFresh(false)
      setResult(null)
      return
    }
    setHoldGlance(false)
  }

  function removeQuery(id: string) {
    sneakPeekApi.deleteQuery(id)
      .then(() => refreshQueries())
      .then(() => {
        if (openQueryId === id) setOpenQueryId(null)
      })
      .catch(() => {})
  }

  function removeVersion(queryId: string, versionId: string) {
    sneakPeekApi.deleteVersion(queryId, versionId)
      .then(() => refreshQueries())
      .catch(() => {})
  }

  function savedReport(value: SneakPeekGlance) {
    const report = { ...value } as SneakPeekGlance & { fresh?: boolean }
    delete report.fresh
    return report
  }

  function saveSelection() {
    if (!selection || saving) return
    if (result && boundStamp !== selectionStamp(selection)) return
    const sent = selection
    setSaving(true)
    sneakPeekApi.saveQuery({
      ...(openQueryId ? { query_id: openQueryId } : {}),
      ...(openVersionId ? { version_id: openVersionId } : {}),
      ...sent,
      ...(result ? { report: savedReport(result) } : {}),
    }).then((saved) => {
      setOpenQueryId(saved.id)
      if (saved.version_id) setOpenVersionId(saved.version_id)
      rememberOpened({
        teacher_id: sent.teacher_id,
        date_from: sent.date_from,
        date_to: sent.date_to,
        selected_dates: sent.selected_dates || [],
        narrowed_days: sent.narrowed_days || [],
        saved_at: saved.saved_at,
      })
      const wrote = Boolean(saved.version_id)
        || openedSavedAt == null
        || saved.saved_at !== openedSavedAt
      if (wrote) setFresh(false)
      return refreshQueries()
    })
      .catch(() => {})
      .finally(() => setSaving(false))
  }

  useEffect(() => {
    if (holdGlance) return
    if (!teacher || !from || !to || to < from) {
      setResult(null)
      return
    }
    const maxDays = requestConfig?.max_days_per_request
    if (maxDays && spanDays(from, to) > maxDays) {
      setResult(null)
      setSpanError(`Maximale Anzahl Tage pro Anfrage: ${maxDays}`)
      return
    }
    setSpanError(null)
    if (!selection) {
      setResult(null)
      return
    }
    const stamp = selectionStamp(selection)
    let cancelled = false
    sneakPeekApi.glance(selection)
      .then((data) => {
        if (cancelled) return
        setResult(data)
        setBoundStamp(stamp)
      })
      .catch(() => {
        if (!cancelled) setResult(null)
      })
    return () => {
      cancelled = true
    }
  }, [teacher, from, to, requestConfig?.max_days_per_request, removedDates, glanceNonce, holdGlance, pinnedNarrowing]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (holdGlanceRef.current) return
    setRemovedDates([])
    setPinnedNarrowing(null)
  }, [teacher?.id, from, to])

  const lessonStamp = calendarDays
    .map((day) => `${day.date}:${(day.lessons ?? []).map((lesson) => lesson.entry_id).join(',')}`)
    .join(';')

  useEffect(() => {
    if (!from || !to || !monthsReady) return
    const next = lessonsFor(calendarDays, panelDates, removedDates, pinnedNarrowing)
    setSelectedLessons((prev) => (sameLessons(prev, next) ? prev : next))
  }, [from, to, monthsReady, lessonStamp, removedDates, pinnedNarrowing, calendarDays, panelDates])

  const days = withoutRemovedDates(result?.days ?? [], removedDates)
  const glanceMatches = !!selection && boundStamp === selectionStamp(selection)
  const columns = WEEKDAYS.map((label, index) => ({
    label,
    days: days.filter((day) => weekdayIndex(day.date) === index),
    minimum: seriesMinimum(days.filter((day) => weekdayIndex(day.date) === index)),
  }))

  function excludeDay(date: string) {
    setRemovedDates((prev) => (prev.includes(date) ? prev : [...prev, date]))
    setPinnedNarrowing((prev) => {
      const next = (prev ?? []).filter((row) => row.date !== date)
      return next.length ? next : null
    })
  }

  function includeDay(date: string) {
    noteSelectionChange()
    setRemovedDates((prev) => prev.filter((item) => item !== date))
    setPinnedNarrowing((prev) => {
      const next = (prev ?? []).filter((row) => row.date !== date)
      return next.length ? next : null
    })
  }

  function selectAllLessons() {
    noteSelectionChange()
    setRemovedDates([])
    setPinnedNarrowing(null)
  }

  function deselectAllLessons() {
    noteSelectionChange()
    const withLessons = panelDates.filter((date) => requestableOn(calendarDays, date).length)
    setRemovedDates((prev) => [...new Set([...prev, ...withLessons])])
    setPinnedNarrowing(null)
  }

  function toggleLesson(lesson: CalendarLesson) {
    if (!isSelectableLesson(lesson)) return
    noteSelectionChange()
    const key = `${lesson.entry_id}|${lesson.date}|${lesson.start_time || ''}`
    const exists = selectedLessons.some((item) => lessonKey(item) === key)
    const next = exists
      ? selectedLessons.filter((item) => lessonKey(item) !== key)
      : [...selectedLessons, toSelectedLesson(lesson)]
    const dayLeft = next.some((item) => item.date === lesson.date)
    const removed = dayLeft
      ? removedDates.filter((date) => date !== lesson.date)
      : (removedDates.includes(lesson.date) ? removedDates : [...removedDates, lesson.date])
    const requestableByDate: Record<string, string[]> = {}
    const splitIds = new Map<string, Set<string>>()
    for (const date of panelDates) {
      const lessons = requestableOn(calendarDays, date)
      const counts = new Map<string, number>()
      for (const lesson of lessons) counts.set(lesson.entry_id, (counts.get(lesson.entry_id) ?? 0) + 1)
      const split = new Set([...counts].filter(([, count]) => count > 1).map(([id]) => id))
      splitIds.set(date, split)
      requestableByDate[date] = lessons.map((lesson) => splitLessonIdentity(lesson.entry_id, lesson.start_time, split.has(lesson.entry_id)))
    }
    const selectedByDate: Record<string, string[]> = {}
    for (const item of next) {
      if (removed.includes(item.date)) continue
      const ids = selectedByDate[item.date] ?? []
      ids.push(splitLessonIdentity(item.entryId, item.startTime, splitIds.get(item.date)?.has(item.entryId) ?? false))
      selectedByDate[item.date] = ids
    }
    const rows = deriveNarrowing(panelDates, removed, requestableByDate, selectedByDate)
    const loaded = new Set(calendarDays.map((day) => day.date))
    const kept = (pinnedNarrowing ?? []).filter((row) => !removed.includes(row.date) && !loaded.has(row.date))
    const merged = [...kept, ...rows].sort((left, right) => left.date.localeCompare(right.date))
    setRemovedDates(removed)
    setPinnedNarrowing(merged.length ? merged : null)
  }

  const shownDate = selectedDate || from
  const currentDay = calendarDays.find((day) => day.date === shownDate)
  const multiDayData: Record<string, { date: string; lessons: CalendarLesson[]; is_school_day: boolean }> = {}
  for (const date of panelDates) {
    const day = calendarDays.find((item) => item.date === date)
    multiDayData[date] = {
      date,
      lessons: day?.lessons ?? [],
      is_school_day: day?.is_school_day ?? true,
    }
  }

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <SavedQueries
        queries={queries}
        onOpen={openSaved}
        onOpenVersion={openVersion}
        onDelete={removeQuery}
        onDeleteVersion={removeVersion}
      />
      <div className="mb-4 rounded-xl border border-gray-200 bg-white p-3.5">
        <TeacherLetterDirectory
          title="Lehrer wählen"
          items={teachersQ.data?.teachers ?? []}
          query={teacherSearch}
          onQueryChange={setTeacherSearch}
          selectedId={teacher?.id}
          onSelect={(item) => {
            noteSelectionChange()
            setRemovedDates([])
            setPinnedNarrowing(null)
            setTeacher(item)
          }}
        />
      </div>
      <div className="mb-4 bg-white rounded-lg border px-4 py-3 flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2 bg-gray-100 rounded-lg p-1">
          <button
            type="button"
            onClick={() => {
              noteSelectionChange()
              setRemovedDates([])
              setPinnedNarrowing(null)
              setSelectionMode('single')
              setDateRange(null)
              setResult(null)
            }}
            className={`px-3 py-1.5 rounded-md text-sm font-medium ${
              selectionMode === 'single' ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-600'
            }`}
          >
            Einzelner Tag
          </button>
          <button
            type="button"
            onClick={() => {
              noteSelectionChange()
              setRemovedDates([])
              setPinnedNarrowing(null)
              setSelectionMode('range')
              setSelectedDate('')
              setDateRange(null)
              setRangeStep('from')
              setResult(null)
            }}
            className={`px-3 py-1.5 rounded-md text-sm font-medium ${
              selectionMode === 'range' ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-600'
            }`}
          >
            Zeitraum
          </button>
        </div>
        {selectionMode === 'single' ? (
          <input
            type="date"
            value={selectedDate}
            min={todayLocalISO()}
            onChange={(e) => {
              noteSelectionChange()
              setRemovedDates([])
              setPinnedNarrowing(null)
              setSelectedDate(e.target.value)
            }}
            className="px-3 py-2 border rounded-lg text-sm"
            aria-label="Datum auswählen"
          />
        ) : (
          <div className="flex items-center gap-2">
            <input
              type="date"
              value={dateRange?.from || ''}
              min={todayLocalISO()}
              onChange={(e) => {
                const value = e.target.value
                if (!value) return
                noteSelectionChange()
                setRemovedDates([])
                setPinnedNarrowing(null)
                const nextTo = dateRange?.to && dateRange.to >= value ? dateRange.to : value
                setDateRange({ from: value, to: nextTo })
              }}
              className="px-3 py-2 border rounded-lg text-sm"
              aria-label="Von-Datum"
            />
            <span className="text-gray-400">→</span>
            <input
              type="date"
              value={dateRange?.to || ''}
              min={todayLocalISO()}
              onChange={(e) => {
                const value = e.target.value
                if (!value || !dateRange?.from) return
                noteSelectionChange()
                setRemovedDates([])
                setPinnedNarrowing(null)
                const nextFrom = value < dateRange.from ? value : dateRange.from
                setDateRange({ from: nextFrom, to: value })
              }}
              className="px-3 py-2 border rounded-lg text-sm"
              aria-label="Bis-Datum"
            />
          </div>
        )}
        <ThresholdField draft={draft} onDraft={setDraft} onCommit={commitThreshold} />
        {teacher && from && to && (
          <button
            type="button"
            onClick={saveSelection}
            disabled={saving || (!!result && !glanceMatches)}
            className="px-3 py-2 rounded-lg bg-gray-900 text-sm text-white"
          >
            Speichern
          </button>
        )}
        {openQueryId && (
          <button
            type="button"
            onClick={recalculate}
            className="px-3 py-2 rounded-lg border text-sm"
          >
            Neu rechnen
          </button>
        )}
        {result && teacher && from && to && (
          <button
            type="button"
            onClick={() => {
              if (glanceMatches) setPrintOpen(true)
            }}
            disabled={!result || !glanceMatches}
            className="px-3 py-2 rounded-lg border text-sm"
          >
            Drucken
          </button>
        )}
      </div>
      {spanError && <p className="mb-3 text-sm text-red-700">{spanError}</p>}
      {teacher && (
        <div className={from && to ? 'grid grid-cols-1 lg:grid-cols-3 gap-6' : undefined}>
          <div className={from && to ? 'lg:col-span-2' : undefined}>
            <CalendarView
              month={month}
              year={year}
              days={monthQ.data?.days ?? []}
              selectedDate={selectedDate}
              onDateSelect={(date) => {
                if (date < todayLocalISO()) return
                noteSelectionChange()
                setRemovedDates([])
                setPinnedNarrowing(null)
                if (selectionMode === 'single') {
                  setSelectedDate(date)
                  return
                }
                if (rangeStep === 'from' || !dateRange?.from) {
                  setDateRange({ from: date, to: date })
                  setRangeStep('to')
                  return
                }
                const start = date < dateRange.from ? date : dateRange.from
                const end = date < dateRange.from ? dateRange.from : date
                setDateRange({ from: start, to: end })
                setRangeStep('from')
              }}
              onPrevMonth={() => {
                if (month === 1) {
                  setMonth(12)
                  setYear((y) => y - 1)
                } else setMonth((m) => m - 1)
              }}
              onNextMonth={() => {
                if (month === 12) {
                  setMonth(1)
                  setYear((y) => y + 1)
                } else setMonth((m) => m + 1)
              }}
              onToday={() => {
                const today = new Date()
                setYear(today.getFullYear())
                setMonth(today.getMonth() + 1)
              }}
              selectionMode={selectionMode}
              dateRange={dateRange}
            />
          </div>
          {from && to && (
            <DayLessonsPanel
              date={shownDate}
              lessons={currentDay?.lessons ?? []}
              selectedLessons={selectedLessons}
              onLessonToggle={toggleLesson}
              onSelectAll={selectAllLessons}
              onDeselectAll={deselectAllLessons}
              onSelectDay={includeDay}
              onDeselectDay={(date) => {
                excludeDay(date)
                setFresh(false)
                setHoldGlance(false)
              }}
              selectedDates={panelDates}
              multiDayData={multiDayData}
            />
          )}
        </div>
      )}
      {printOpen && result && teacher && from && to && glanceMatches && (
        <SneakPeekPrintView
          absent={`${teacher.last_name} ${teacher.first_name}`.trim()}
          from={from}
          to={to}
          threshold={result.threshold}
          selectedDates={[]}
          days={days}
        />
      )}
      {result && fresh && <p>gerade gerechnet</p>}
      {result && (
        <p className="mt-6 text-xs text-gray-500">
          Jede Karte trägt ihr Datum. «Tag entfernen» nimmt den Tag aus der Auswahl.
        </p>
      )}
      {result && (
        <div className="mt-2 flex items-start gap-3 overflow-x-auto pb-2">
          {columns.map((column) => (
            <section key={column.label} className="w-max shrink-0">
              <button type="button" className="mb-2 text-sm font-medium text-gray-500">
                {column.label} mindestens {column.minimum ?? '—'}
              </button>
              {column.days.map((day) => (
                <DayCell
                  key={day.date}
                  day={day}
                  onRemove={() => {
                    excludeDay(day.date)
                    setFresh(false)
                    setHoldGlance(false)
                  }}
                />
              ))}
            </section>
          ))}
        </div>
      )}
    </div>
  )
}
